import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, RotateCcw, Check, Clock, User, CreditCard, ShoppingBag, Banknote, CirclePercent, QrCode, Landmark, Package, Trash2, Settings } from 'lucide-react';
import './OrderConfirmation.css';
import apiService from '../services/api';

const OrderConfirmation = ({ orderData, onClose, onConfirm, onModify }) => {
  const { user } = useAuth();
  const displayUserName = user?.firstName || user?.username || user?.name || user?.email || orderData.userName || 'Usuario';
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedOrderType, setSelectedOrderType] = useState('sale'); // Default to 'sale'
  const [createdOrderNumber, setCreatedOrderNumber] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // LUCIDE ICONS VERSION - Clean, professional look
  const paymentMethods = [
    { value: 'efectivo', label: 'Efectivo', IconComponent: Banknote },
    { value: 'debito', label: 'Débito', IconComponent: CreditCard },
    { value: 'credito', label: 'Crédito', IconComponent: CreditCard, SecondIcon: CirclePercent },
    { value: 'qr', label: 'QR', IconComponent: QrCode },
    { value: 'transferencia', label: 'Transferencia', IconComponent: Landmark },
    { value: 'rappi', label: 'Rappi' }, // Using background image instead of IconComponent
    { value: 'pedidosya', label: 'Pedidos Ya' } // Using background image instead of IconComponent
  ];

  // Order types with corresponding icons and descriptions (English values, Spanish labels)
  const orderTypes = [
    { 
      value: 'sale', 
      label: 'Venta', 
      IconComponent: ShoppingBag, 
      description: 'Venta regular de productos',
      color: '#27ae60'
    },
    { 
      value: 'waste_expired', 
      label: 'Desecho/Vencido', 
      IconComponent: Trash2, 
      description: 'Productos vencidos o desechados',
      color: '#e74c3c'
    },
    { 
      value: 'stock_adjustment', 
      label: 'Ajuste de Stock', 
      IconComponent: Settings, 
      description: 'Ajuste de inventario',
      color: '#f39c12'
    }
  ];

  const formatCurrency = (amount) => {
    // Amount is already in pesos (converted from cents), just round it
    return `$${Math.round(amount)}`;
  };

  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('es-AR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateTotal = () => {
    return orderData.items.reduce((sum, item) => 
      sum + (item.price * item.quantity), 0
    );
  };

  const handleConfirm = async () => {
    // For sale orders, payment method is required
    if (selectedOrderType === 'sale' && !selectedPaymentMethod) {
      alert('Por favor selecciona un método de pago para las ventas.');
      return;
    }
    
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Prepare order data for backend
      const orderPayload = {
        userName: orderData.userName,
        orderType: selectedOrderType,
        paymentMethod: selectedOrderType === 'sale' ? selectedPaymentMethod : null,
        items: orderData.items.map(item => ({
          name:      item.name,
          quantity:  item.quantity,
          price:     item.price,
          id:        item.productId || item.id || null,  // PyME ProductId
          _raw:      item._raw || null,                  // full PyME product record
        })),
        discount: 0,
        notes: `${getOrderTypeDescription(selectedOrderType)} - Order created at ${formatTime()}`
      };

      console.log('Sending order to backend:', orderPayload);

      // Send order to backend
      const response = await apiService.createOrder(orderPayload);
      
      console.log('Order created successfully:', response);

      // Store the order number from backend response
      setCreatedOrderNumber(response.orderNumber);
      setShowSuccess(true);
      
      // Auto-close after 2 seconds and clear order
      setTimeout(() => {
        onConfirm(selectedPaymentMethod, selectedOrderType);
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error creating order:', error);
      alert('Error al crear el pedido. Por favor, intente nuevamente.');
      setIsSubmitting(false);
    }
  };

  const handleModify = () => {
    onModify();
    onClose();
  };

  // Helper function to get order type description
  const getOrderTypeDescription = (orderType) => {
    const type = orderTypes.find(t => t.value === orderType);
    return type ? type.description : 'Orden';
  };

  // Helper function to check if payment method should be disabled
  const isPaymentMethodDisabled = () => {
    return selectedOrderType !== 'sale';
  };

  if (showSuccess) {
    return (
      <div className="order-confirmation-overlay">
        <div className="success-container">
          <div className="success-icon">
            <Check size={80} strokeWidth={3} />
          </div>
          <h2>¡Pedido Confirmado!</h2>
          <p className="success-order-number">
            Pedido {createdOrderNumber || orderData.orderNumber}
          </p>
          <p className="success-message">El pedido ha sido guardado exitosamente</p>
          <div className="success-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-confirmation-overlay">
      <div className="order-confirmation-container">
        {/* Header */}
        <div className="order-confirmation-header">
          <div className="header-content">
            <ShoppingBag size={28} />
            <h2>Confirmar Pedido</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Order Info */}
        <div className="order-confirmation-body">
          <div className="order-info-grid">
            <div className="info-card info-card-items">
              <div className="info-icon">
                <ShoppingBag size={20} />
              </div>
              <div className="info-content">
                <span className="info-label">Productos</span>
                <span className="info-items-count">{orderData.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
              </div>
            </div>

            <div className="info-card info-card-time">
              <div className="info-icon">
                <Clock size={20} />
              </div>
              <div className="info-content">
                <span className="info-label">Hora</span>
                <span className="info-time">{formatTime()}</span>
              </div>
            </div>

            <div className="info-card info-card-user">
              <div className="info-icon">
                <User size={20} />
              </div>
              <div className="info-content">
                <span className="info-label">Usuario</span>
                <span className="info-user">{displayUserName}</span>
              </div>
            </div>
          </div>

          {/* Order Type Selection */}
          <div className="order-types-grid">
            {orderTypes.map(type => (
              <button
                key={type.value}
                className={`order-type-btn ${selectedOrderType === type.value ? 'selected' : ''}`}
                onClick={() => setSelectedOrderType(type.value)}
                style={{ '--type-color': type.color }}
              >
                <div className="order-type-icon">
                  <type.IconComponent size={20} strokeWidth={2} />
                </div>
                <span className="order-type-label">{type.label}</span>
              </button>
            ))}
          </div>

          {/* Order Items */}
          <div className="order-items-section">
            <h3>Detalle del Pedido</h3>
            <div className="order-items-list">
              {orderData.items.map((item, index) => (
                <div key={index} className="order-item-row">
                  <div className="item-quantity">{item.quantity}x</div>
                  <div className="item-name">{item.name}</div>
                  <div className="item-subtotal">
                    {formatCurrency(item.price * item.quantity)}
                  </div>
                </div>
              ))}
            </div>

            <div className="order-total">
              <span>Total</span>
              <span className="total-amount">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className={`payment-method-section ${isPaymentMethodDisabled() ? 'disabled' : ''}`}>
            <div className="payment-methods-grid">
              {paymentMethods.map(method => (
                <button
                  key={method.value}
                  className={`payment-method-btn ${selectedPaymentMethod === method.value ? 'selected' : ''} ${method.value === 'rappi' || method.value === 'pedidosya' ? 'custom-icon' : ''} ${isPaymentMethodDisabled() ? 'disabled' : ''}`}
                  onClick={() => !isPaymentMethodDisabled() && setSelectedPaymentMethod(method.value)}
                  disabled={isPaymentMethodDisabled()}
                >
                  {method.value === 'rappi' || method.value === 'pedidosya' ? (
                    <div className="payment-icon-wrapper custom-svg">
                      <div 
                        className={`payment-icon-logo ${method.value}-logo`}
                        style={{
                          backgroundImage: `url(${require(`../assets/images/${method.value === 'rappi' ? 'Rappi' : 'PedidosYa'}.svg`)})`,
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          width: '32px',
                          height: '32px'
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="payment-icon-wrapper">
                        <method.IconComponent className="payment-icon-lucide" size={24} strokeWidth={2} />
                        {method.SecondIcon && (
                          <method.SecondIcon className="payment-icon-badge" size={12} strokeWidth={2.5} />
                        )}
                      </div>
                      <span className="payment-label">{method.label}</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="order-confirmation-footer">
          <button className="btn-secondary-action order-cancel-btn" onClick={handleModify}>
            <RotateCcw size={16} />
            Modificar Pedido
          </button>
          <button 
            className="btn-primary-action order-save-btn" 
            onClick={handleConfirm}
            disabled={(selectedOrderType === 'sale' && !selectedPaymentMethod) || isSubmitting}
          >
            <Check size={16} />
            {isSubmitting ? 'Guardando...' : 'Confirmar y Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
